import * as React from 'react';
import { useUploadContent, IUploadConfig, IUploadOptions } from '@frontend/app/hooks/useUploadContent';

const DEFAULT_CONFIG: IUploadConfig = {
  serviceName: 'message',
  parentFolder: 'inline-videos',
  isTemp: false,
  keepFileName: true,
};

const DEFAULT_OPTIONS: IUploadOptions = {
  imageCompressionEnabled: false, // Don't compress videos
};

export interface IVideoUploadResult {
  url: string;
  name: string;
  size: number;
}

export const useVideoUpload = (config?: Partial<IUploadConfig>, options?: IUploadOptions) => {
  const uploadConfig = React.useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);

  const uploadOptions = React.useMemo(() => ({ ...DEFAULT_OPTIONS, ...options }), [options]);

  const { upload, error, content, isUploading } = useUploadContent(uploadConfig);

  const uploadVideo = React.useCallback(
    async (file: File): Promise<IVideoUploadResult> => {
      const fileUrl = await upload(file, 'video', uploadOptions);

      return {
        url: fileUrl,
        name: file.name,
        size: file.size,
      };
    },
    [upload, uploadOptions],
  );

  const validateVideoFile = React.useCallback((file: File): string | null => {
    const allowedTypes = [
      'video/mp4',
      'video/webm',
      'video/ogg',
      'video/quicktime', // .mov
      'video/x-msvideo', // .avi
    ];

    if (!allowedTypes.includes(file.type)) {
      return 'Please select a valid video file (MP4, WebM, OGG, MOV, or AVI)';
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return 'Video file size must be less than 50MB';
    }

    return null;
  }, []);

  return {
    uploadVideo,
    validateVideoFile,
    isUploading,
    error,
    content,
  };
};
